import { addressSchema } from "@redotech/redo-customer-common/customer";
import { ReturnProductSchema_ } from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import { FieldId } from "@redotech/redo-return-core-common/return-flow-answers";
import { ExtendedWarrantySchema } from "@redotech/redo-return-warranties-common/extended-warranty";
import { PurchaseChannel } from "@redotech/redo-return-warranties-common/warranties";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const SharedSourceFields = { channelName: z.string().optional() };

const OnlineSourceSchema = z.object({
  purchaseChannel: z.literal(PurchaseChannel.ONLINE),
  orderId: zExt.objectId(),
  referenceId: z.string(),
  ...SharedSourceFields,
});

const RetailSourceSchema = z.object({
  purchaseChannel: z.literal(PurchaseChannel.RETAIL),
  ...SharedSourceFields,
});

export const WarrantyRegistrationSourceSchema = z.discriminatedUnion(
  "purchaseChannel",
  [OnlineSourceSchema, RetailSourceSchema],
);

export type WarrantyRegistrationSource = z.infer<
  typeof WarrantyRegistrationSourceSchema
>;

export const WarrantyRegistrationResponseType = [
  "images",
  "text_area",
  "videos",
  "multiple_choice",
  "date",
] as const;

export type WarrantyRegistrationResponseType =
  (typeof WarrantyRegistrationResponseType)[number];

// Coerced: CRDB stores `product` as jsonb, which returns ISO strings for
// dates. Scoped here so the shared draft-return schema stays strict.
export const WarrantyProductSchema = ReturnProductSchema_.extend({
  deliveredOn: z.coerce.date(),
  fulfilledOn: z.coerce.date(),
  lastFulfillmentUpdateOn: z.coerce.date(),
});

export const WarrantyRegistrationSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  email: z.string(),
  name: z.string(),
  product: WarrantyProductSchema,
  // Dual-written as `[product]` for every registration. Source of truth going
  // forward; optional until existing documents are backfilled.
  products: z.array(WarrantyProductSchema).optional(),
  responses: z.array(
    z.object({
      type: createEnum(WarrantyRegistrationResponseType),
      value: z.any(),
      message: z.string(),
      fieldId: createEnum(FieldId).nullish(),
    }),
  ),
  customer: zExt.objectId().nullish(),
  customerData: z
    .object({
      _id: zExt.objectId(),
      firstName: z.string(),
      lastName: z.string(),
      address: addressSchema.nullish(),
    })
    .nullish(),
  returnId: zExt.objectId().nullish(),
  extendedWarranty: ExtendedWarrantySchema.nullish(),
  optInMarketing: z.boolean().nullish(),
  discountCode: z.string().nullish(),
  source: WarrantyRegistrationSourceSchema.nullish(),
  /** Purchase date of the registered item, from the order-date flow input. */
  purchaseDate: z.coerce.date().nullish(),
  createdAt: z.coerce.date(),
  // PaymentIntent is excluded since it contains sensitive data
  requiresProductValidation: z.boolean().nullish(),
  customerProvidedProductName: z.string().nullish(),
  customerProvidedProductPhotos: z.array(z.string()).nullish(),
  customerProvidedProductPrice: z.string().nullish(),
});

export type WarrantyRegistration = z.infer<typeof WarrantyRegistrationSchema>;

export const REDO_WARRANTY_REGISTRATION_ID_ATTRIBUTE_KEY =
  "_redo_warranty_registration_id";

export type WarrantyProduct = z.infer<typeof WarrantyProductSchema>;

// Transition helper while readers migrate from `product` to `products`:
// documents created before the dual-write have no `products` field.
export function getRegistrationProducts<T>(registration: {
  product: T;
  products?: readonly T[] | null;
}): T[] {
  return registration.products?.length
    ? [...registration.products]
    : [registration.product];
}

export function getPrimaryRegistrationProduct<T>(registration: {
  product: T;
  products?: readonly T[] | null;
}): T {
  return registration.products?.[0] ?? registration.product;
}

export function getCustomerName(registration: WarrantyRegistration): string {
  const { firstName, lastName } = registration.customerData ?? {};
  if (!firstName) {
    return "Unknown";
  }
  return lastName ? `${firstName} ${lastName}` : firstName;
}
